import styled, { useTheme } from "styled-components";
import { useContext, useState } from "react";
import { components } from "react-select";
import { Select } from "../select/Select";
import { ColumnOptionsContext } from "./ColumnOptions";
import { Star20Filled } from "@fluentui/react-icons";
import { Setting } from "../types/types";

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";

export const ViewSelect = ({ className }: { className?: string }) => {
  const theme = useTheme();
  const {
    views,
    selectedView,
    isViewLoading,
    onChangeView,
    strings,
    hideViewOptionsFeatures,
    usePlaceholder,
    hasUnsavedChanges,
    id,
  } = useContext(ColumnOptionsContext);
  // Holds the view the user asked to switch to while there are unsaved changes.
  const [pendingViewId, setPendingViewId] = useState<{
    viewId: Setting["userAppSettingId"] | null;
  } | null>(null);

  const options = views.map((s) => {
    return {
      label: s.userAppSettingName,
      value: s.userAppSettingId,
      isDefault: s.isDefault,
    };
  });

  let selectedOption: {
    value: Setting["userAppSettingId"] | "default";
    label: string;
    isDefault?: boolean;
  } | null = null;
  if (selectedView !== null && selectedView !== undefined) {
    selectedOption = {
      value: selectedView.userAppSettingId,
      label: selectedView.userAppSettingName,
      isDefault: selectedView.isDefault,
    };
  }
  //
  else if (!usePlaceholder) {
    selectedOption = {
      value: "default",
      label: strings.defaultViewLabel,
    };
  }

  const isLoading = isViewLoading;
  const isDisabled = isLoading;

  const isDark = (theme as any)?.isDark || false;

  const requestChangeView = (viewId: Setting["userAppSettingId"] | null) => {
    if (viewId === (selectedView?.userAppSettingId ?? null)) return;
    if (hasUnsavedChanges) {
      setPendingViewId({ viewId });
      return;
    }
    onChangeView(viewId);
  };

  return (
    <ViewSelectContainer
      className={className}
      data-testid="column-options-view-select"
    >
      {!hideViewOptionsFeatures && (
        <>
          <Select
            value={selectedOption}
            onChange={(o: any) => {
              requestChangeView(o ? o.value : null);
            }}
            options={options}
            noOptionsMessage={() => strings.viewSelectNoViewsLabel}
            placeholder={strings.viewSelectPlaceholder}
            isSearchable={options.length > 8}
            isClearable={!!selectedView}
            loadingMessage={() => strings.viewSelectLoadingLabel}
            isDisabled={isDisabled}
            isLoading={isLoading}
            styles={{
              menu: (provided) => ({
                ...provided,
                backgroundColor: isDark ? "#292827" : "white",
                zIndex: 9999,
              }),
              menuList: (provided) => ({
                ...provided,
                backgroundColor: isDark ? "#292827" : "white",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? isDark
                    ? "#3b3a39"
                    : "rgb(237, 235, 233)"
                  : state.isFocused
                  ? isDark
                    ? "#201f1e"
                    : "rgb(243, 242, 241)"
                  : isDark
                  ? "#292827"
                  : "white",
                "&:hover": {
                  backgroundColor: state.isSelected
                    ? isDark
                      ? "#3b3a39"
                      : "rgb(237, 235, 233)"
                    : isDark
                    ? "#201f1e"
                    : "rgb(243, 242, 241)",
                },
              }),
            }}
            components={{
              Option: CustomOption,
              SingleValue: CustomSingleValue,
              DropdownIndicator: (props: any) => (
                <components.DropdownIndicator {...props}>
                  <span style={{ fontSize: "12px" }}>▼</span>
                </components.DropdownIndicator>
              ),
            }}
            id={id ? `${id}-view-select` : undefined}
            className="view-select"
          />
          <Dialog
            open={pendingViewId !== null}
            onOpenChange={(_, data) => !data.open && setPendingViewId(null)}
            modalType="alert"
          >
            <DialogSurface data-testid="column-options-discard-dialog">
              <DialogBody>
                <DialogTitle>{strings.unsavedChangesLabel}</DialogTitle>
                <DialogContent>{strings.discardChangesConfirm}</DialogContent>
                <DialogActions>
                  <Button
                    appearance="secondary"
                    onClick={() => setPendingViewId(null)}
                  >
                    {strings.cancelButtonLabel}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      if (pendingViewId) onChangeView(pendingViewId.viewId);
                      setPendingViewId(null);
                    }}
                  >
                    {strings.discardButtonLabel}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </>
      )}
    </ViewSelectContainer>
  );
};

const ViewSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <OptionContent>
        {props.data.isDefault && <StarIcon />}
        {props.children}
      </OptionContent>
    </components.Option>
  );
};

const CustomSingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <SingleValueContent>
        {props.data.isDefault && <StarIcon />}
        {props.children}
      </SingleValueContent>
    </components.SingleValue>
  );
};

const OptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SingleValueContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StarIcon = styled(Star20Filled)`
  color: ${(props) => props.theme.palette.themePrimary};
  font-size: 16px;
`;
